import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Bell,
  BellRing,
  BellOff,
  Download,
  FileDown,
  Mail,
  MessageSquareText,
  FileSignature,
  LogOut,
  Trash2,
  CheckCheck,
  RefreshCcw,
  Search,
  X,
  Smartphone,
} from "lucide-react";
import { BRAND } from "../lib/brand";
import {
  apiFetch,
  clearToken,
  getToken,
  downloadBlob,
  urlBase64ToUint8Array,
} from "../lib/api";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_) {
    return iso;
  }
}

function summaryLine(s) {
  const d = s.data || {};
  if (s.kind === "waiver") {
    return d.fullName || "Unnamed rider";
  }
  if (d.fullName) return d.fullName;
  const bits = [];
  if (d.people) bits.push(`${d.people} ppl`);
  if (d.atvs) bits.push(`${d.atvs} ATV`);
  if (d.sxs) bits.push(`${d.sxs} SxS`);
  if (d.duration) bits.push(d.duration);
  return bits.join(" · ") || "Text-booking lead";
}

function subLine(s) {
  const d = s.data || {};
  if (s.kind === "waiver") {
    return [d.phone, d.email].filter(Boolean).join(" · ");
  }
  // For leads, prefer showing phone + email so admin can act immediately
  const contact = [d.phone, d.email].filter(Boolean).join(" · ");
  const when = d.datetime || d.datetime_pref || "";
  return [contact, when].filter(Boolean).join(" — ");
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null); // full submission
  const [user, setUser] = useState(null);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const audioRef = useRef(null);
  const lastCountRef = useRef(0);
  const [installEvent, setInstallEvent] = useState(null);
  const [installed, setInstalled] = useState(false);

  const doLogout = useCallback(() => {
    clearToken();
    nav("/admin/login");
  }, [nav]);

  const refresh = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/admin/submissions`);
      setItems(res.items || []);
      if (typeof res.unread === "number") {
        // Chime when new unread arrives compared to last count
        if (res.unread > lastCountRef.current && lastCountRef.current !== 0) {
          try {
            if (audioRef.current) audioRef.current.play().catch(() => {});
          } catch (_) {
            /* audio blocked by browser autoplay */
          }
        }
        lastCountRef.current = res.unread;
        setUnread(res.unread);
      }
    } catch (e) {
      if (String(e.message) === "unauthorized") doLogout();
    } finally {
      setLoading(false);
    }
  }, [doLogout]);

  useEffect(() => {
    if (!getToken()) {
      nav("/admin/login");
      return;
    }
    (async () => {
      try {
        const me = await apiFetch(`/api/auth/me`);
        setUser(me.user);
      } catch (_) {
        /* handled by refresh() below */
      }
      refresh();
    })();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [nav, refresh]);

  // Update tab title with unread count + OS app-icon badge
  useEffect(() => {
    const base = "Rockstar Admin";
    document.title = unread > 0 ? `(${unread}) ${base}` : base;
    if ("setAppBadge" in navigator) {
      if (unread > 0) navigator.setAppBadge(unread).catch(() => {});
      else navigator.clearAppBadge && navigator.clearAppBadge().catch(() => {});
    }
  }, [unread]);

  // PWA install prompt handling
  useEffect(() => {
    const onBefore = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setInstallEvent(null);
    };
    // Already running as standalone?
    if (
      window.matchMedia && window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true
    ) {
      setInstalled(true);
    }
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const installApp = async () => {
    if (installEvent) {
      installEvent.prompt();
      const { outcome } = await installEvent.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setInstallEvent(null);
      return;
    }
    // iOS Safari fallback — no beforeinstallprompt event
    alert(
      "To install on iPhone/iPad:\n\n1. Tap the Share icon (square with up arrow)\n2. Scroll and tap 'Add to Home Screen'\n3. Tap 'Add' — a Rockstar Admin icon appears with unread badge support."
    );
  };

  const exportCsv = async () => {
    await downloadBlob(`/api/admin/export.csv`, `rockstar-submissions.csv`);
  };

  // Push subscription registration
  const checkPush = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    try {
      const reg = await navigator.serviceWorker.getRegistration("/admin-sw.js");
      if (!reg) return setPushOn(false);
      const sub = await reg.pushManager.getSubscription();
      setPushOn(!!sub);
    } catch (_) {
      setPushOn(false);
    }
  }, []);

  useEffect(() => {
    checkPush();
  }, [checkPush]);

  const enablePush = async () => {
    setPushBusy(true);
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        alert("Your browser doesn't support push notifications.");
        return;
      }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        alert("Please allow notifications in your browser settings.");
        return;
      }
      const reg =
        (await navigator.serviceWorker.getRegistration("/admin-sw.js")) ||
        (await navigator.serviceWorker.register("/admin-sw.js"));
      await navigator.serviceWorker.ready;
      const { publicKey } = await apiFetch(`/api/admin/push/vapid-public-key`);
      if (!publicKey) {
        alert("Push not configured on server.");
        return;
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      const json = sub.toJSON();
      await apiFetch(`/api/admin/push/subscribe`, {
        method: "POST",
        body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys }),
      });
      setPushOn(true);
    } catch (e) {
      console.error(e);
      alert("Couldn't enable push notifications.");
    } finally {
      setPushBusy(false);
    }
  };

  const disablePush = async () => {
    setPushBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration("/admin-sw.js");
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          const json = sub.toJSON();
          try {
            await apiFetch(`/api/admin/push/unsubscribe`, {
              method: "POST",
              body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys }),
            });
          } catch (_) {
            /* server may already have removed it */
          }
          await sub.unsubscribe();
        }
      }
      setPushOn(false);
    } finally {
      setPushBusy(false);
    }
  };

  const openDetail = async (id) => {
    try {
      const full = await apiFetch(`/api/admin/submissions/${id}`);
      setSelected(full);
      if (!full.read) {
        await apiFetch(`/api/admin/submissions/${id}/read`, { method: "POST" });
        refresh();
      }
    } catch (e) {
      if (String(e.message) === "unauthorized") doLogout();
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission? This cannot be undone.")) return;
    await apiFetch(`/api/admin/submissions/${id}`, { method: "DELETE" });
    if (selected && selected.id === id) setSelected(null);
    refresh();
  };

  const markAllRead = async () => {
    await apiFetch(`/api/admin/submissions/mark-all-read`, { method: "POST" });
    refresh();
  };

  const downloadPdf = async (s) => {
    const d = s.data || {};
    const name = (d.fullName || d.people || s.id).toString().replace(/[^a-z0-9]+/gi, "-");
    await downloadBlob(`/api/admin/submissions/${s.id}/pdf`, `rockstar-${s.kind}-${name}.pdf`);
  };

  const mailto = (s) => {
    const d = s.data || {};
    const to = d.email || BRAND.email;
    const first = (d.fullName || "").split(" ")[0] || "there";
    const subject =
      s.kind === "waiver"
        ? `Rockstar ATV Rentals — your waiver is confirmed`
        : `Rockstar ATV Rentals — your booking request`;
    const body =
      s.kind === "waiver"
        ? `Hi ${first},\n\nThanks for signing the rider waiver — you're all set to ride with Rockstar ATV Rentals.\n\nReply here or text ${BRAND.phone} with your preferred date/time and we'll lock it in.\n\n— Rockstar ATV Rentals`
        : `Hi ${first || "rider"},\n\nThanks for reaching out to Rockstar ATV Rentals!\n\nWe received your booking request:\n${d.people ? "• People: " + d.people + "\n" : ""}${d.duration ? "• Duration: " + d.duration + "\n" : ""}${d.datetime || d.datetime_pref ? "• Date/Time: " + (d.datetime || d.datetime_pref) + "\n" : ""}\nReply here or text ${BRAND.phone} to confirm.\n\n— Rockstar ATV Rentals`;
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const filtered = useMemo(() => {
    let arr = items;
    if (filter === "waivers") arr = arr.filter((i) => i.kind === "waiver");
    else if (filter === "leads") arr = arr.filter((i) => i.kind === "lead");
    else if (filter === "unread") arr = arr.filter((i) => !i.read);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter((i) => {
        const d = i.data || {};
        return [
          d.fullName,
          d.email,
          d.phone,
          d.people,
          d.datetime,
          d.datetime_pref,
          d.duration,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      });
    }
    return arr;
  }, [items, filter, search]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-dashboard">
      {/* Notification chime */}
      <audio
        ref={audioRef}
        src="data:audio/wav;base64,UklGRlwEAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YTgEAAAAAAA/AH8AvwD/AL8AfwA/AAAAwP+A/0D/AP9A/4D/wP8AAAAA"
        preload="auto"
      />

      {/* Header */}
      <header
        className="sticky top-0 z-30 border-b border-white/10 bg-black/90 backdrop-blur"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2" data-testid="admin-brand">
              <div className="bg-black rounded-full p-0.5 ring-1 ring-white/15">
                <img src={BRAND.logo} alt="Rockstar" className="h-8 w-8 rounded-full" />
              </div>
              <div>
                <div className="font-display text-white text-sm leading-none">ROCKSTAR</div>
                <div className="text-[9px] tracking-[0.28em] text-[color:var(--primary)] font-mono">
                  ADMIN COMMAND
                </div>
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            {!installed && (
              <button
                onClick={installApp}
                className="hidden sm:inline-flex items-center gap-1.5 border border-white/20 hover:border-[color:var(--primary)] hover:text-[color:var(--primary)] px-3 py-2 text-[10px] uppercase tracking-widest font-mono font-bold transition-colors"
                data-testid="admin-install"
                title="Install as an app on this device"
              >
                <Smartphone size={13} /> Install App
              </button>
            )}
            <button
              onClick={pushOn ? disablePush : enablePush}
              disabled={pushBusy}
              className={`hidden sm:inline-flex items-center gap-1.5 border px-3 py-2 text-[10px] uppercase tracking-widest font-mono font-bold transition-colors ${
                pushOn ? "border-[color:var(--primary)] text-[color:var(--primary)]" : "border-white/20 text-white/70 hover:text-white"
              }`}
              data-testid="admin-push-toggle"
            >
              {pushOn ? <BellRing size={13} /> : <BellOff size={13} />}
              {pushOn ? "PUSH ON" : "ENABLE PUSH"}
            </button>
            <button
              onClick={exportCsv}
              className="inline-flex items-center gap-1.5 border border-white/20 hover:text-[color:var(--primary)] px-3 py-2 text-[10px] uppercase tracking-widest font-mono font-bold transition-colors"
              data-testid="admin-export-csv"
              title="Download all submissions as CSV"
            >
              <FileDown size={13} /> <span className="hidden sm:inline">CSV</span>
            </button>
            <button
              onClick={refresh}
              className="inline-flex items-center gap-1.5 border border-white/20 hover:text-[color:var(--primary)] px-3 py-2 text-[10px] uppercase tracking-widest font-mono font-bold transition-colors"
              data-testid="admin-refresh"
              aria-label="Refresh"
            >
              <RefreshCcw size={13} /> <span className="hidden sm:inline">REFRESH</span>
            </button>
            <button
              onClick={doLogout}
              className="inline-flex items-center gap-1.5 border border-white/20 hover:text-[color:var(--primary)] px-3 py-2 text-[10px] uppercase tracking-widest font-mono font-bold transition-colors"
              data-testid="admin-logout"
            >
              <LogOut size={13} /> <span className="hidden sm:inline">LOGOUT</span>
            </button>
          </div>
        </div>
      </header>

      {/* Sub-header: filters + unread badge */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Bell size={20} className="text-[color:var(--primary)]" />
            {unread > 0 && (
              <span
                className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[color:var(--primary)] text-white text-[10px] font-bold flex items-center justify-center"
                data-testid="admin-unread-badge"
              >
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </div>
          <div>
            <p className="font-display uppercase text-lg leading-none">Submissions</p>
            <p className="text-[10px] uppercase tracking-widest font-mono text-white/50 mt-1" data-testid="admin-summary">
              {items.length} total · <span className="text-[color:var(--primary)]">{unread}</span> unread
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 md:ml-auto">
          {["all", "unread", "waivers", "leads"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-mono font-bold border transition-colors ${
                filter === f
                  ? "border-[color:var(--primary)] bg-[color:var(--primary)]/15 text-[color:var(--primary)]"
                  : "border-white/15 text-white/70 hover:text-white"
              }`}
              data-testid={`admin-filter-${f}`}
            >
              {f}
            </button>
          ))}
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, phone…"
              className="pl-8 pr-3 py-1.5 bg-[color:var(--bg-elev)] border border-white/15 text-xs text-white outline-none focus:border-[color:var(--primary)] transition-colors w-56"
              data-testid="admin-search"
            />
          </div>
          {unread > 0 && (
            <button
              onClick={markAllRead}
              className="inline-flex items-center gap-1.5 border border-white/15 hover:text-[color:var(--primary)] px-3 py-1.5 text-[10px] uppercase tracking-widest font-mono font-bold transition-colors"
              data-testid="admin-mark-all-read"
            >
              <CheckCheck size={13} /> Mark all read
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <main className="max-w-[1400px] mx-auto px-4 md:px-8 pb-16">
        {loading ? (
          <p className="text-white/40 text-sm">Loading submissions…</p>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-white/15 py-16 text-center" data-testid="admin-empty">
            <p className="text-white/50 text-sm">No submissions match your filters yet.</p>
            <p className="text-[10px] uppercase tracking-widest font-mono text-white/30 mt-2">
              When someone completes a waiver or text-booking form, it will appear here in real time.
            </p>
          </div>
        ) : (
          <div className="grid gap-2">
            {filtered.map((s) => (
              <button
                key={s.id}
                onClick={() => openDetail(s.id)}
                className={`text-left border transition-colors px-4 py-3 md:py-4 flex flex-col md:flex-row md:items-center gap-3 ${
                  s.read ? "border-white/10 hover:border-white/25" : "border-[color:var(--primary)]/50 bg-[color:var(--primary)]/5"
                }`}
                data-testid={`admin-item-${s.id}`}
              >
                <div className="flex items-center gap-2.5 md:w-40">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-1 text-[9px] uppercase tracking-widest font-mono font-bold ${
                      s.kind === "waiver"
                        ? "bg-[color:var(--primary)]/20 text-[color:var(--primary)]"
                        : "bg-white/10 text-white"
                    }`}
                  >
                    {s.kind === "waiver" ? <FileSignature size={11} /> : <MessageSquareText size={11} />}
                    {s.kind}
                  </span>
                  {!s.read && (
                    <span className="w-2 h-2 rounded-full bg-[color:var(--primary)] animate-pulse" title="Unread" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-white text-base leading-tight truncate">{summaryLine(s)}</p>
                  <p className="text-xs text-white/50 truncate mt-0.5">{subLine(s)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] uppercase tracking-widest font-mono text-white/40">
                    {fmtDate(s.created_at)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      {/* Detail Drawer */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-stretch md:items-center justify-end md:justify-center"
            onClick={() => setSelected(null)}
            data-testid="admin-detail-overlay"
          >
            <motion.div
              initial={{ x: 60, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 60, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full md:w-[820px] max-h-[100vh] md:max-h-[92vh] bg-[color:var(--bg-elev)] border border-white/15 overflow-y-auto"
              data-lenis-prevent
              data-testid="admin-detail"
            >
              <SubmissionDetail
                sub={selected}
                onClose={() => setSelected(null)}
                onDownload={() => downloadPdf(selected)}
                onEmail={() => mailto(selected)}
                onDelete={() => remove(selected.id)}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SubmissionDetail({ sub, onClose, onDownload, onEmail, onDelete }) {
  const d = sub.data || {};
  const isWaiver = sub.kind === "waiver";
  return (
    <div>
      <div className="sticky top-0 flex items-center justify-between px-5 md:px-7 py-3 bg-black border-b border-white/10 z-10">
        <div className="flex items-center gap-2">
          {isWaiver ? (
            <FileSignature size={14} className="text-[color:var(--primary)]" />
          ) : (
            <MessageSquareText size={14} className="text-[color:var(--primary)]" />
          )}
          <p className="font-display text-white text-sm tracking-wide">
            {isWaiver ? "SIGNED WAIVER" : "TEXT BOOKING LEAD"}
          </p>
          <span className="text-[9px] uppercase tracking-widest font-mono text-white/40 ml-2">
            {fmtDate(sub.created_at)}
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 text-white/70 hover:text-white transition-colors"
          data-testid="admin-detail-close"
        >
          <X size={18} />
        </button>
      </div>

      <div className="px-5 md:px-7 py-5 space-y-6">
        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onDownload}
            className="inline-flex items-center gap-2 bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white font-bold uppercase tracking-[0.16em] text-[10px] px-4 py-2.5 transition-colors"
            data-testid="admin-detail-download"
          >
            <Download size={13} /> Save as PDF
          </button>
          <button
            onClick={onEmail}
            className="inline-flex items-center gap-2 border border-white/25 hover:bg-white hover:text-black text-white font-bold uppercase tracking-[0.16em] text-[10px] px-4 py-2.5 transition-colors"
            data-testid="admin-detail-email"
          >
            <Mail size={13} /> Email client
          </button>
          {(d.phone || d.email) && (
            <a
              href={`sms:${(d.phone || BRAND.phoneRaw).replace(/[^0-9+]/g, "")}`}
              className="inline-flex items-center gap-2 border border-white/25 hover:bg-white hover:text-black text-white font-bold uppercase tracking-[0.16em] text-[10px] px-4 py-2.5 transition-colors"
              data-testid="admin-detail-text"
            >
              <MessageSquareText size={13} /> Text client
            </a>
          )}
          <button
            onClick={onDelete}
            className="ml-auto inline-flex items-center gap-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 font-bold uppercase tracking-[0.16em] text-[10px] px-4 py-2.5 transition-colors"
            data-testid="admin-detail-delete"
          >
            <Trash2 size={13} /> Delete
          </button>
        </div>

        {/* Fields */}
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
          {isWaiver ? (
            <>
              <Field label="Full Name" value={d.fullName} />
              <Field label="Date of Birth" value={d.dob} />
              <Field label="Phone" value={d.phone} />
              <Field label="Email" value={d.email} />
              <Field
                label="Address"
                value={[d.address, d.city, d.state, d.zip].filter(Boolean).join(", ")}
                full
              />
              <Field label="Helmet" value={d.helmet} full />
              <Field label="Agreed to Release" value={d.agreeRelease ? "YES" : "NO"} />
              {Array.isArray(d.minors) && d.minors.length > 0 && (
                <div className="md:col-span-2">
                  <p className="text-[9px] uppercase tracking-[0.26em] font-mono font-bold text-white/60 mb-1.5">
                    Minors ({d.minors.length})
                  </p>
                  <ul className="text-sm text-white/80 space-y-1">
                    {d.minors.map((m, i) => (
                      <li key={i}>
                        #{i + 1} · {m.name || "—"} · DOB {m.birthdate || "—"} · {m.relation || "—"}
                      </li>
                    ))}
                  </ul>
                  <p className="text-[9px] uppercase tracking-[0.26em] font-mono font-bold text-white/60 mt-3">
                    Guardian Consent
                  </p>
                  <p className="text-sm text-white/80">{d.guardianConsent ? "YES" : "NO"}</p>
                </div>
              )}
            </>
          ) : (
            <>
              <Field label="ATVs" value={d.atvs} />
              <Field label="Side-by-sides" value={d.sxs} />
              <Field label="Talon Seater" value={d.seater} />
              <Field label="Total People" value={d.people} />
              <Field label="Kids / Ages" value={d.kids} />
              <Field label="Tour Duration" value={d.duration} />
              <Field
                label="Preferred Date & Time"
                value={d.datetime || d.datetime_pref}
                full
              />
              {d.composed_message && (
                <div className="md:col-span-2">
                  <p className="text-[9px] uppercase tracking-[0.26em] font-mono font-bold text-white/60 mb-1.5">
                    Composed SMS text
                  </p>
                  <pre className="text-xs text-white/70 whitespace-pre-wrap bg-black border border-white/10 p-3">
                    {d.composed_message}
                  </pre>
                </div>
              )}
            </>
          )}
        </div>

        {/* Media */}
        {isWaiver && (
          <div className="grid md:grid-cols-2 gap-5 pt-2">
            {d.signature && (
              <div>
                <p className="text-[9px] uppercase tracking-[0.26em] font-mono font-bold text-white/60 mb-1.5">
                  Signature
                </p>
                <div className="bg-white p-2 border border-white/15">
                  <img src={d.signature} alt="signature" className="w-full h-auto" data-testid="admin-detail-signature" />
                </div>
              </div>
            )}
            {d.idImage && (
              <div>
                <p className="text-[9px] uppercase tracking-[0.26em] font-mono font-bold text-white/60 mb-1.5">
                  Photo ID {d.idFileName ? `· ${d.idFileName}` : ""}
                </p>
                <div className="bg-black border border-white/15">
                  <img
                    src={d.idImage}
                    alt="id"
                    className="w-full h-auto max-h-[420px] object-contain"
                    data-testid="admin-detail-id"
                  />
                </div>
                <a
                  href={d.idImage}
                  download={d.idFileName || "id-photo.jpg"}
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono text-[color:var(--primary)] mt-2"
                >
                  <Download size={11} /> Download raw photo
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, full = false }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <p className="text-[9px] uppercase tracking-[0.26em] font-mono font-bold text-white/60 mb-1">
        {label}
      </p>
      <p className="text-sm text-white/90 break-words">{value || <span className="text-white/30">—</span>}</p>
    </div>
  );
}
